"use client";

import { useEffect, useMemo, useState } from "react";
import { inputClass } from "@/components/creatorVoice/CreatorVoiceUI";
import { CAPTION_LANGUAGES, CAPTION_FONTS, baseFontFamily, googleFontsStylesheetUrl, googleFontsSpecimenUrl, type CaptionLanguage } from "@/lib/creatorVoice/captionFonts";
import { buildCues, buildSrt, buildVtt, buildAss, downloadTextFile } from "@/lib/creatorVoice/captionFormats";
import { trackEvent } from "@/lib/analytics/track";

export default function CaptionGeneratorTool() {
  const [language, setLanguage] = useState<CaptionLanguage>("english");
  const [font, setFont] = useState(CAPTION_FONTS.english[0]);
  const [text, setText] = useState("");
  const [wpm, setWpm] = useState(160);

  const fonts = CAPTION_FONTS[language];

  useEffect(() => {
    setFont(fonts[0]);
  }, [language, fonts]);

  // Load every font for the selected language as a real <link> stylesheet
  // so the preview and the font dropdown itself render in the actual
  // typefaces, not just list their names.
  useEffect(() => {
    const href = googleFontsStylesheetUrl(fonts);
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, [fonts]);

  const lines = useMemo(() => text.split("\n").map((l) => l.trim()).filter(Boolean), [text]);
  const cues = useMemo(() => buildCues(lines, wpm), [lines, wpm]);
  const totalDuration = cues.length ? cues[cues.length - 1].end : 0;

  function download(format: "srt" | "vtt" | "ass") {
    if (!cues.length) return;
    trackEvent("ai_tool_click", { tool: "caption_generator", format });
    if (format === "srt") downloadTextFile("captions.srt", buildSrt(cues));
    else if (format === "vtt") downloadTextFile("captions.vtt", buildVtt(cues));
    else downloadTextFile("captions.ass", buildAss(cues, baseFontFamily(font)));
  }

  return (
    <div>
      <div className="grid gap-6 lg:grid-cols-2">
        <div>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Caption text (one line = one caption)</label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={12}
            placeholder={"Type or paste your captions, one line per caption.\nEach line becomes its own timed subtitle cue."}
            className={`${inputClass} resize-none`}
          />
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{lines.length} caption{lines.length === 1 ? "" : "s"} · ~{totalDuration.toFixed(1)}s total</p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Language</label>
            <select value={language} onChange={(e) => setLanguage(e.target.value as CaptionLanguage)} className={inputClass}>
              {CAPTION_LANGUAGES.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Font</label>
            <select value={font} onChange={(e) => setFont(e.target.value)} className={inputClass} style={{ fontFamily: `"${baseFontFamily(font)}"` }}>
              {fonts.map((f) => (
                <option key={f} value={f} style={{ fontFamily: `"${baseFontFamily(f)}"` }}>
                  {f}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              SRT/VTT don't carry font info — only the ASS export embeds this font choice.
            </p>
            <p className="mt-1 rounded-lg bg-amber-50 px-2 py-1.5 text-xs text-amber-700 dark:bg-amber-500/10 dark:text-amber-400">
              ⚠ Your video editor needs this font installed on your computer to render it correctly — the subtitle
              file only stores the font's name, not the font itself.{" "}
              <a href={googleFontsSpecimenUrl(font)} target="_blank" rel="noopener noreferrer" className="font-semibold underline">
                Download &amp; install {baseFontFamily(font)} →
              </a>
            </p>
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Reading speed: {wpm} words/min</label>
            <input type="range" min={100} max={220} step={10} value={wpm} onChange={(e) => setWpm(parseInt(e.target.value))} className="mt-2 w-full accent-rose-500" />
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-[#0f172a]">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">Preview</p>
            <p className="mt-2 text-lg text-slate-900 dark:text-white" style={{ fontFamily: `"${baseFontFamily(font)}"` }}>
              {lines[0] || "Your caption text will preview here"}
            </p>
          </div>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap justify-end gap-2">
        <button
          type="button"
          onClick={() => download("srt")}
          disabled={!cues.length}
          className="rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 transition hover:border-rose-300 disabled:opacity-50 dark:border-slate-700 dark:text-slate-200"
        >
          Download .srt
        </button>
        <button
          type="button"
          onClick={() => download("vtt")}
          disabled={!cues.length}
          className="rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 transition hover:border-rose-300 disabled:opacity-50 dark:border-slate-700 dark:text-slate-200"
        >
          Download .vtt
        </button>
        <button
          type="button"
          onClick={() => download("ass")}
          disabled={!cues.length}
          className="rounded-full bg-rose-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:opacity-50"
        >
          Download .ass (with font)
        </button>
      </div>
    </div>
  );
}
